import { useState } from 'react';

import { Checkbox } from 'shared/ui/Checkbox/Checkbox';

import { Box } from './Box';

import styled from '@emotion/styled';

const CheckboxGroupContainer = styled(Box)<{
  align?: 'vertical' | 'horizontal';
}>`
  display: flex;
  flex-direction: ${({ align }) => (align === 'vertical' ? 'column' : 'row')};
  gap: ${({ align }) => (align === 'vertical' ? '26px' : '43px')};
`;

export type CheckboxGroupOption<T extends string | number | undefined> = {
  label: string;
  id: T;
};

type Props<T extends string | number> = {
  options: CheckboxGroupOption<T>[];
  multiply?: boolean;
  align: 'vertical' | 'horizontal';
  onChange: (selected: T[]) => void;
  value?: T[];
  errorMessage?: string;
  labelGap?: number;
};

export const CheckboxGroup = <T extends string | number>({
  options,
  multiply,
  align,
  onChange,
  value = [],
  errorMessage,
  labelGap,
}: Props<T>) => {
  const [selectedValues, setSelectedValues] = useState(value);

  const handleChange = (value: T, checked: boolean) => {
    if (multiply) {
      const selected = selectedValues.includes(value)
        ? selectedValues.filter((item) => item !== value)
        : [...selectedValues, value];

      setSelectedValues(selected);

      onChange(selected);

      return;
    }

    setSelectedValues(checked ? [value] : []);

    onChange(checked ? [value] : []);
  };

  return (
    <CheckboxGroupContainer align={align}>
      {options?.map((option) => (
        <Checkbox
          labelGap={labelGap}
          key={option.id}
          checked={selectedValues.includes(option.id)}
          onChange={(checked) => handleChange(option.id, checked)}
          label={option.label}
        />
      ))}
      {errorMessage && (
        <Box className='error' mt={2} color='red'>
          {errorMessage}
        </Box>
      )}
    </CheckboxGroupContainer>
  );
};
